import { useState, useEffect, useRef } from 'react'
import {
  Phone,
  PhoneOff,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Monitor,
  MonitorOff,
  Volume2,
  Maximize2,
  Minimize2,
  Settings,
  X,
  Wifi,
  WifiOff,
  AlertCircle
} from 'lucide-react'
import { CallState } from '../../services/MediaService'

interface CallControlsProps {
  callState: CallState
  onEndCall: () => void
  onToggleMute: () => void
  onToggleVideo: () => void
  onToggleScreen: () => void
  onToggleFullscreen?: () => void
  localStream?: MediaStream | null
  remoteStream?: MediaStream | null
  isMuted?: boolean
  isVideoEnabled?: boolean
  isScreenSharing?: boolean
  isFullscreen?: boolean
  connectionQuality?: 'excellent' | 'good' | 'poor' | 'unknown'
}

export function CallControls({
  callState,
  onEndCall,
  onToggleMute,
  onToggleVideo,
  onToggleScreen,
  onToggleFullscreen,
  localStream,
  isMuted = false,
  isVideoEnabled = true,
  isScreenSharing = false,
  isFullscreen = false,
  connectionQuality = 'unknown'
}: CallControlsProps) {
  const [callDuration, setCallDuration] = useState('00:00')
  const [showSettings, setShowSettings] = useState(false)
  const [audioLevel, setAudioLevel] = useState(0)
  const animationFrameRef = useRef<number>()
  const audioContextRef = useRef<AudioContext>()
  const analyserRef = useRef<AnalyserNode>()

  // Update call duration timer
  useEffect(() => {
    if (!callState.isInCall || !callState.callStartTime) return

    const updateDuration = () => {
      const now = Date.now()
      const start = callState.callStartTime!.getTime()
      const duration = Math.floor((now - start) / 1000)

      const hours = Math.floor(duration / 3600)
      const minutes = Math.floor((duration % 3600) / 60)
      const seconds = duration % 60

      if (hours > 0) {
        setCallDuration(
          `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        )
      } else {
        setCallDuration(
          `${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`
        )
      }
    }

    updateDuration()
    const interval = setInterval(updateDuration, 1000)

    return () => clearInterval(interval)
  }, [callState.isInCall, callState.callStartTime])

  // Monitor audio levels for visual feedback
  useEffect(() => {
    if (!localStream || isMuted) {
      setAudioLevel(0)
      return
    }

    const audioTrack = localStream.getAudioTracks()[0]
    if (!audioTrack) return

    try {
      const audioContext = new AudioContext()
      const analyser = audioContext.createAnalyser()
      const source = audioContext.createMediaStreamSource(localStream)

      source.connect(analyser)
      analyser.fftSize = 256

      audioContextRef.current = audioContext
      analyserRef.current = analyser

      const dataArray = new Uint8Array(analyser.frequencyBinCount)

      const updateLevel = () => {
        if (!analyserRef.current) return

        analyserRef.current.getByteFrequencyData(dataArray)
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length
        setAudioLevel(Math.min(100, (average / 128) * 100))

        animationFrameRef.current = requestAnimationFrame(updateLevel)
      }

      updateLevel()
    } catch (error) {
      console.warn('Failed to setup audio monitoring:', error)
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [localStream, isMuted])

  const getConnectionIcon = () => {
    switch (connectionQuality) {
      case 'excellent':
        return <Wifi className="w-4 h-4 text-success-500" />
      case 'good':
        return <Wifi className="w-4 h-4 text-warning-500" />
      case 'poor':
        return <WifiOff className="w-4 h-4 text-error-500" />
      default:
        return <AlertCircle className="w-4 h-4 text-secondary-400" />
    }
  }

  const getCallTypeIcon = () => {
    if (callState.callType === 'video') {
      return <Video className="w-4 h-4" />
    } else if (callState.callType === 'screen') {
      return <Monitor className="w-4 h-4" />
    } else {
      return <Phone className="w-4 h-4" />
    }
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800 z-[60]"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      {/* Call Info Bar */}
      <div className="px-3 sm:px-4 py-2 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
          <div className="flex items-center space-x-2 text-white min-w-0">
            {getCallTypeIcon()}
            <span className="text-sm font-medium truncate max-w-[120px] sm:max-w-none">
              {/* CRITICAL FIX: Proper alias resolution for call controls */}
              {callState.activePeerAlias || `User_${callState.activePeer?.slice(-8) || 'Unknown'}`}
            </span>
          </div>

          <div className="flex items-center space-x-1 sm:space-x-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-xs sm:text-sm text-gray-300">{callDuration}</span>
          </div>

          <div className="flex items-center space-x-1 hidden sm:flex" title={`Connection: ${connectionQuality}`}>
            {getConnectionIcon()}
          </div>
        </div>

        {/* Audio Level Indicator - Hidden on mobile to save space */}
        {!isMuted && (
          <div className="hidden sm:flex items-center space-x-2">
            <Volume2 className="w-4 h-4 text-gray-400" />
            <div className="w-16 sm:w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-yellow-500 transition-all duration-100"
                style={{ width: `${audioLevel}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Control Buttons */}
      <div className="px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex items-center justify-center space-x-2 sm:space-x-3">
          {/* Mute/Unmute */}
          <button
            onClick={onToggleMute}
            className={`p-3 sm:p-4 rounded-full transition-all mobile-touch-target ${
              isMuted
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <MicOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Mic className="w-4 h-4 sm:w-5 sm:h-5" />}
          </button>

          {/* Video Toggle (only for video calls) */}
          {callState.callType === 'video' && (
            <button
              onClick={onToggleVideo}
              className={`p-3 sm:p-4 rounded-full transition-all mobile-touch-target ${
                !isVideoEnabled
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
              title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            >
              {isVideoEnabled ? <Video className="w-4 h-4 sm:w-5 sm:h-5" /> : <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          )}

          {/* Screen Share Toggle */}
          <button
            onClick={onToggleScreen}
            className={`p-3 sm:p-4 rounded-full transition-all mobile-touch-target ${
              isScreenSharing
                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
            title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
          >
            {isScreenSharing ? <MonitorOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Monitor className="w-4 h-4 sm:w-5 sm:h-5" />}
          </button>

          {/* End Call - Prominent */}
          <button
            onClick={onEndCall}
            className="p-3 sm:p-4 px-6 sm:px-8 bg-red-600 hover:bg-red-700 text-white rounded-full transition-all transform hover:scale-105 mobile-touch-target"
            title="End call"
          >
            <PhoneOff className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          {/* Fullscreen Toggle (for video/screen) - Hidden on small screens */}
          {(callState.callType === 'video' || isScreenSharing) && onToggleFullscreen && (
            <button
              onClick={onToggleFullscreen}
              className="p-3 sm:p-4 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-all mobile-touch-target hidden sm:flex"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          )}

          {/* Settings - Hidden on small screens */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-3 sm:p-4 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-all mobile-touch-target hidden sm:flex"
            title="Call settings"
          >
            <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Mobile-optimized layout for small screens */}
        <div className="mt-2 sm:mt-3 flex items-center justify-center text-xs text-gray-400 sm:hidden">
          <span>Tap and hold for more options</span>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="absolute bottom-full left-0 right-0 bg-gray-900 border-t border-gray-800 p-3 sm:p-4 mb-1 mobile-safe-area">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium">Call Settings</h3>
            <button
              onClick={() => setShowSettings(false)}
              className="p-1 hover:bg-gray-800 rounded text-gray-400 mobile-touch-target"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3">
            {/* Audio Settings */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Noise Suppression</span>
              <button className="px-3 py-1 bg-gray-700 rounded text-xs text-white">
                Enabled
              </button>
            </div>

            {/* Video Quality */}
            {callState.callType === 'video' && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Video Quality</span>
                <select className="px-3 py-1 bg-gray-700 rounded text-xs text-white">
                  <option>Auto</option>
                  <option>HD (720p)</option>
                  <option>SD (480p)</option>
                  <option>Low (360p)</option>
                </select>
              </div>
            )}

            {/* Network Stats */}
            <div className="pt-2 border-t border-gray-800">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">Latency</span>
                <span className="text-gray-300">32ms</span>
              </div>
              <div className="flex items-center justify-between text-xs mt-1">
                <span className="text-gray-400">Packet Loss</span>
                <span className="text-gray-300">0.1%</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}